import {
  createStackNavigator,
  StackScreenProps,
} from '@react-navigation/stack';
import CryptoJs from 'crypto-js';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';

import OnboardingModal from '@components/Modals/OnboardingModal';
import { modalStyle } from '@navigation/helpers';
import { SCOPED_INVITE_TYPES } from '@navigation/scopedInviteTypes';
import { getTaskJoinRequestIdFromUrl } from '@navigation/taskInviteUrlParams';
import CreateProfile from '@screens/Auth/CreateProfile.web';
import { EmailLogin } from '@screens/Auth/EmailLogin.web';
import { ForgotPassword } from '@screens/Auth/ForgotPassword.web';
import { InvitationPreview } from '@screens/Auth/InvitationPreview.web';
import InvitedUser from '@screens/Auth/InvitedUser';
import InvitedUserForRegister from '@screens/Auth/InvitedUserForRegister';
import { LoadingScreen } from '@screens/Auth/LoadingScreen.web';
import Onboarding from '@screens/Auth/Onboarding.web';
import RegisterForm from '@screens/Auth/RegisterForm.web';
import { ResetPassword } from '@screens/Auth/ResetPassword.web';
import { ImagesPreviewNotLogin } from '@screens/Chats/ImagesPreviewNotLogin.web';
import ProjectInvitationPreviewScreenWeb from '@screens/Projects/ProjectInvitationPreviewScreen.web';
import ProjectInvitationPreviewScreenDummy from '@screens/Projects/ProjectInvitationPreviewScreenDummy.web';
import { TaskInvitationPreviewScreenWeb } from '@screens/Tasks/TaskInvitationPreviewScreen.web';
import TeamInvitationPreviewScreenWeb from '@screens/Teams/TeamInvitationPreviewScreen.web';
import {
  isLegacyMediaShareSegment,
  sanitizeShareToken,
} from '@utils/shareLinkToken';

export const secretKey = process.env.SHARE_MEDIA_URL_SECRET_KEY;

export const setImagesAndUser = () => {
  const segment = decodeURIComponent(window.location.pathname)?.replace(
    '/images-preview/',
    ''
  );

  if (!isLegacyMediaShareSegment(segment)) {
    const token = sanitizeShareToken(segment);
    if (!token) {
      // No usable token in this URL (e.g. a bare /images-preview/). Skip
      // persisting an empty share_token — ImagesPreviewNotLogin treats it as
      // falsy and falls through to the legacy images_path branch, which we'd
      // have just cleared below, leaving the screen blank with no data.
      window.sessionStorage.removeItem('share_token');
      return;
    }
    window.sessionStorage.setItem('share_token', token);
    // Clear any legacy keys from a previously opened legacy link so a stale
    // images_path/user_* set doesn't leak into this token-based session.
    window.sessionStorage.removeItem('images_path');
    window.sessionStorage.removeItem('user_avatar');
    window.sessionStorage.removeItem('user_name');
    return;
  }

  // Clear a stale share_token from a previously opened token link so
  // ImagesPreviewNotLogin doesn't skip this legacy branch.
  window.sessionStorage.removeItem('share_token');

  const decryptedData = CryptoJs.AES.decrypt(segment, secretKey);
  const path = decryptedData.toString(CryptoJs.enc.Utf8);

  const imagesPath = path?.substring(0, path?.indexOf(';'));
  window.sessionStorage.setItem('images_path', imagesPath);

  const user = path?.substring(path?.indexOf(';') + 1);
  const userAvatar = user?.substring(0, user?.indexOf(';'));
  const userName = user?.substring(user?.indexOf(';') + 1);
  window.sessionStorage.setItem('user_avatar', userAvatar);
  window.sessionStorage.setItem('user_name', userName);
};

type AuthStackParamList = {
  onboarding: undefined;
  'onboarding-modal': undefined;
  'invited-user': undefined;
  'invited-user-for-register': undefined;
  'onboarding-web': { isLogin?: boolean };
  login: { fromReset?: boolean };
  'password-reset': undefined;
  'create-profile': { socialSignUp?: boolean };
  register: { routeName: string };
  'forgot-password': { email: string };
  LoadingScreen: undefined;
  'images-preview': undefined;
  'project-invitation-preview': undefined;
  'project-invitation-preview-dummy': undefined;
  'task-invitation-preview': undefined;
  'invitation-preview': undefined;
  'team-invitation-preview': undefined;
};

export type AuthStackScreenProps<T extends keyof AuthStackParamList> =
  StackScreenProps<AuthStackParamList, T>;

const Stack = createStackNavigator<AuthStackParamList>();

const AuthStack: React.FC = () => {
  const { t } = useTranslation();
  const title = t('shared:appName');
  const [isImageCarouselVisible, setImageCarouselVisible] = useState(false);

  const queryParameters = new URLSearchParams(window.location.search);
  const path = window.location.pathname;
  const isGroupInviteLink = path.startsWith('/invite/group');
  // Capture URL-only values before falling back to sessionStorage so routing
  // decisions can distinguish "no token in this URL" from a stale stored token.
  const urlToken =
    queryParameters.get('invitation_token') ?? queryParameters.get('token');
  const urlId = queryParameters.get('id') ?? '';
  const urlRequestId = getTaskJoinRequestIdFromUrl(window.location.search);
  let invitationToken = urlToken;
  let type = queryParameters.get('type') ?? (isGroupInviteLink ? 'chat' : '');
  let id = urlId;
  if (invitationToken == null) {
    invitationToken = window.sessionStorage.getItem('invitation_token') ?? '';
    type = type || (window.sessionStorage.getItem('link_type') ?? '');
    id = id || (window.sessionStorage.getItem('task_or_project_id') ?? '');
    // this from when user signup -> create team -> invite user ->
    // invited user click invitation link from email inbox, so I put it here -- april TA-5972
    if (path.startsWith('/account_invitations/')) {
      const code = path.substring('/account_invitations/'.length);
      window.sessionStorage.setItem('invitation_token', code);
      window.sessionStorage.setItem('link_type', 'team');
    }
  } else {
    window.sessionStorage.setItem('invitation_token', invitationToken);
    window.sessionStorage.setItem('link_type', type);
    window.sessionStorage.setItem('task_or_project_id', id);
    // Task email invite links carry the pending join-request id in the URL.
    // Persist it so app-stack.web.tsx can call joinRequestedTask once the
    // user logs in from the preview screen (TA-10474 — previously lost,
    // so TaskUser was never created for existing users on this path).
    window.sessionStorage.setItem('task_join_request_id', urlRequestId);
    // Clear stale team_preview_uuid so the preview screen always uses
    // the token path when a fresh invitation_token is present in the URL.
    if (type === 'team') {
      window.sessionStorage.removeItem('team_preview_uuid');
    }
    if (type === 'chat') {
      window.sessionStorage.setItem(
        'pending_chat_invitation_token',
        invitationToken
      );
      if (id) {
        window.sessionStorage.setItem('pending_chat_invitation_id', id);
      } else {
        window.sessionStorage.removeItem('pending_chat_invitation_id');
      }
    }
  }

  const isRegister =
    window.location.pathname === '/register' ||
    window.location.pathname === '/register/signup-with-email';
  const isLogin = window.location.pathname === '/log-in';

  const isScopedInvite =
    SCOPED_INVITE_TYPES.includes(type) && !!invitationToken;

  const isInvitedUser =
    window.location.pathname === '/invited-user' ||
    window.location.pathname.startsWith('/account_invitations');

  const isInvitedUserForRegister =
    window.location.pathname === '/invited-user-register';

  const isImagesPreview =
    window.location.pathname.startsWith('/images-preview/');

  useEffect(() => {
    if (isImagesPreview) {
      setImagesAndUser();
      setImageCarouselVisible(isImagesPreview);
    }
  }, [isImagesPreview]);

  // Detect public team detail URLs shared as invitation links.
  // Pattern: /my-account/subscriptionPlanDetail/{uuid}/TeamDetails
  const teamDetailMatch = path.match(
    /^\/my-account\/subscriptionPlanDetail\/([0-9a-f-]{36})\/TeamDetails$/i
  );
  if (teamDetailMatch) {
    const teamId = teamDetailMatch[1];
    window.sessionStorage.setItem('team_preview_uuid', teamId);
    window.sessionStorage.setItem('task_or_project_id', teamId);
    window.sessionStorage.setItem('link_type', 'team');
    // No token in this URL — clear any stale email invite token so the preview
    // screen doesn't query invitationDetails with a leftover token.
    window.sessionStorage.removeItem('invitation_token');
    window.sessionStorage.removeItem('team_invite_auto_join');
    invitationToken = '';
  }
  const isTeamLinkPreview = !!teamDetailMatch;

  const getInitialRouteName = () => {
    if (isLogin) {
      return 'login';
    }
    // Dummy preview — accessible at /project-invitation-preview-dummy (no token needed)
    if (path === '/project-invitation-preview-dummy') {
      return 'project-invitation-preview-dummy';
    }
    // Public team detail URL shared as an invitation link
    if (isTeamLinkPreview) {
      return 'team-invitation-preview';
    }
    // Project invitation links show the preview screen regardless of pathname
    if (type === 'project' && invitationToken) {
      return 'project-invitation-preview';
    }
    // Task invitation links show the task invitation preview screen
    if (type === 'task' && invitationToken) {
      return 'task-invitation-preview';
    }
    // Token-based team invitation: /invited-user?invitation_token=xxx&type=team
    if (
      window.location.pathname === '/invited-user' &&
      type === 'team' &&
      invitationToken &&
      !id
    ) {
      return 'team-invitation-preview';
    }
    // Link-based team invitation via /invited-user?type=team&id={uuid} (no token in URL)
    if (
      window.location.pathname === '/invited-user' &&
      type === 'team' &&
      urlId &&
      urlToken == null
    ) {
      window.sessionStorage.setItem('team_preview_uuid', urlId);
      window.sessionStorage.setItem('task_or_project_id', urlId);
      window.sessionStorage.setItem('link_type', 'team');
      // Clear any stale email invite token from a previous session
      window.sessionStorage.removeItem('invitation_token');
      window.sessionStorage.removeItem('team_invite_auto_join');
      return 'team-invitation-preview';
    }
    if (isInvitedUser) {
      return 'invited-user';
    }
    if (isInvitedUserForRegister) {
      return 'invited-user-for-register';
    }
    if (isScopedInvite) {
      return 'invitation-preview';
    }
    // Fallback: token-based team invite on a URL path not handled above.
    // Only check urlToken — using the sessionStorage-sourced invitationToken here
    // would redirect any unrelated page visit while a stale team token lingers.
    if (urlToken && queryParameters.get('type') === 'team') {
      return 'team-invitation-preview';
    }
    if (invitationToken || isRegister) {
      return 'register';
    }
    if (isImageCarouselVisible) {
      return 'images-preview';
    }
    return 'login';
  };

  return (
    <>
      <Stack.Navigator
        screenOptions={{ title }}
        initialRouteName={getInitialRouteName()}>
        <Stack.Screen
          name='login'
          component={EmailLogin}
          options={{
            title:
              'Log In | TaskTag App for Construction Project Communication',
            headerShown: false,
          }}
        />
        <Stack.Screen
          name='LoadingScreen'
          component={LoadingScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name='forgot-password'
          component={ForgotPassword}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name='password-reset'
          component={ResetPassword}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name='register'
          component={RegisterForm}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name='onboarding'
          component={Onboarding}
          options={{ headerShown: false }}
        />

        <Stack.Screen
          name='onboarding-modal'
          component={OnboardingModal}
          options={{ ...modalStyle }}
        />
        <Stack.Screen
          name='onboarding-web'
          component={OnboardingModal}
          options={{
            headerShown: false,
          }}
        />
        <Stack.Screen
          name='invited-user'
          component={InvitedUser}
          options={{
            headerShown: false,
          }}
        />
        <Stack.Screen
          name='invited-user-for-register'
          component={InvitedUserForRegister}
          options={{
            headerShown: false,
          }}
        />
        <Stack.Screen
          name='create-profile'
          component={CreateProfile}
          options={{
            headerShown: false,
          }}
        />
        {isImageCarouselVisible && (
          <Stack.Screen
            name='images-preview'
            component={EmailLogin}
            options={{ headerShown: false }}
          />
        )}
        <Stack.Screen
          name='project-invitation-preview'
          component={ProjectInvitationPreviewScreenWeb}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name='project-invitation-preview-dummy'
          component={ProjectInvitationPreviewScreenDummy}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name='task-invitation-preview'
          component={TaskInvitationPreviewScreenWeb}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name='invitation-preview'
          component={InvitationPreview}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name='team-invitation-preview'
          component={TeamInvitationPreviewScreenWeb}
          options={{
            title: 'Join Team | TaskTag',
            headerShown: false,
          }}
        />
      </Stack.Navigator>
      {Platform.OS === 'web' && isImageCarouselVisible && (
        <ImagesPreviewNotLogin
          onDismiss={() => setImageCarouselVisible(false)}
        />
      )}
    </>
  );
};

export { AuthStack };
